import { Looker40SDK } from "@looker/sdk";
import {
  getParticipantMonthCacheKey,
  participantECGReadingsCache,
} from "./CalendarViewQueryResultsCache";
import { LookerDashboardId } from "generated/studydata/studydata_pb";
import {
  lookerReturnsInvalidResults,
  telemetry,
} from "core/TelemetryController";

/**
 * Queries Looker to get a participant's ECG readings in a given month.
 *
 * The function assumes that the Looker user attributes for participant id,
 * month start date and month end date are set up properly.
 *
 * See instructions below about how to get the Looker query id and query field names
 * used in this function:
 *   go/verily-devices-looker-dashboards
 */
export async function getParticipantECGReadingsDaily(
  sdk: Looker40SDK,
  studyParticipantId: string,
  monthStartDate: Date,
  ecgReadingsEnabled: boolean,
  lookerQueryIdsMap: Map<LookerDashboardId, string>
) {
  let ecgReadingsMap = new Map<string, number>();
  let hasError = false;

  if (!ecgReadingsEnabled) {
    return {
      ecgReadingsMap: ecgReadingsMap,
      hasError: false,
    };
  }

  try {
    const cacheKey = getParticipantMonthCacheKey(
      studyParticipantId,
      monthStartDate
    );

    if (participantECGReadingsCache.has(cacheKey)) {
      const ecgReadingsCached = participantECGReadingsCache.get(cacheKey)!;
      return { ecgReadingsMap: ecgReadingsCached, hasError: false };
    }

    const results = await sdk.ok(
      sdk.run_query({
        query_id: lookerQueryIdsMap.get(
          LookerDashboardId.PARTICIPANT_ECG_READINGS_DAILY_COUNT
        )!,
        result_format: "json",
      })
    );

    if (results && Array.isArray(results)) {
      for (let row of results) {
        /** Week Start date is in ISO 8601 format YYYY-MM-DD */
        const ecgReadingDate =
          row["participant_ecg_readings_daily_count.event_date"];
        const ecgReadingCount =
          row["participant_ecg_readings_daily_count.ecg_readings_count"];
        ecgReadingsMap.set(ecgReadingDate, ecgReadingCount);
      }

      // Put results in cache
      participantECGReadingsCache.set(cacheKey, ecgReadingsMap);
    } else {
      telemetry.logError(new Error(lookerReturnsInvalidResults));
      hasError = true;
    }
  } catch (error) {
    telemetry.logError(error);
    hasError = true;
  }

  return { ecgReadingsMap: ecgReadingsMap, hasError: hasError };
}
